/**
 * Static configuration: planning layers, boundaries, basemaps, geographic constants.
 * Layer numbers are those of the official Hà Nội planning information system
 * (quyhoach.hanoi.gov.vn); colours / legends come from /api/meta (built from the source).
 */

export const HANOI_BBOX = [105.28, 20.56, 106.02, 21.39];
export const HANOI_CENTER = { lat: 21.0278, lng: 105.8342 };
export const MAX_BOUNDS = [[104.75, 20.15], [106.55, 21.8]];
export const DEFAULT_VIEW = { lat: 21.0245, lng: 105.8412, zoom: 12.4 };

export const ORIGIN = location.origin;

/** Main planning layers (one shown at a time). */
export const PLAN_LAYERS = [
  {
    layerNo: 654, key: 'qhpk', icon: 'layout-grid',
    title: 'Quy hoạch phân khu', short: 'QH phân khu 1/2000 – 1/5000',
    sub: 'Chức năng sử dụng đất từng ô · tỷ lệ 1/2000 – 1/5000',
    unit: 'ô đất',
  },
  {
    layerNo: 'gt', key: 'qhgt', icon: 'route', virtual: true,
    title: 'Quy hoạch giao thông', short: 'Quy hoạch giao thông',
    sub: 'Mạng lưới đường theo QH phân khu và QH chung · đường sắt, metro',
    unit: '',
  },
  {
    layerNo: 673, key: 'qhct', icon: 'building',
    title: 'Quy hoạch chi tiết 1/500', short: 'QH chi tiết 1/500',
    sub: 'Các đồ án chi tiết tỷ lệ 1/500 đã số hoá',
    unit: 'ô đất',
  },
  {
    layerNo: 666, key: 'qhc', icon: 'map',
    title: 'Quy hoạch chung Thủ đô', short: 'QH chung Thủ đô – sử dụng đất',
    sub: 'Sử dụng đất theo quy hoạch chung toàn thành phố',
    unit: 'khoanh đất',
  },
  {
    layerNo: 667, key: 'qhcpk', icon: 'shapes',
    title: 'Phân vùng chức năng (QH chung)', short: 'QH chung – phân vùng chức năng',
    sub: 'Nội đô lịch sử, đô thị vệ tinh, vùng sinh thái…',
    unit: 'vùng',
  },
  {
    layerNo: 895, key: 'qh100', icon: 'telescope',
    title: 'QH tổng thể Thủ đô tầm nhìn 100 năm', short: 'QH tổng thể tầm nhìn 100 năm',
    sub: 'Chức năng sử dụng đất · hệ thống thông tin quy hoạch',
    unit: 'khoanh đất',
  },
  {
    layerNo: 'sdd', key: 'khsdd', icon: 'land-plot', virtual: true,
    title: 'Kế hoạch sử dụng đất (Luật Đất đai)', short: 'Kế hoạch sử dụng đất',
    sub: 'KHSDĐ cấp huyện 2018–2024, QHSDĐ đến 2020 · Sở Nông nghiệp và Môi trường',
    unit: '',
  },
];
export const PLAN_BY_NO = Object.fromEntries(PLAN_LAYERS.map((l) => [l.layerNo, l]));
/** Plan value from a hash / select / data attribute ("0" = none, "gt" = transport view). */
export const planFrom = (v) => {
  if (v === null || v === undefined || v === '' || v === '0' || v === 0) return null;
  const n = Number.isFinite(+v) ? +v : String(v);
  return PLAN_BY_NO[n] ? n : undefined;
};

/**
 * Legend of the transport view. Planned roads are derived from the official zone / general
 * plans; rail, metro and construction come from OpenStreetMap (existing situation).
 */
export const TRANSPORT_LEGEND = [
  { kind: 'fill', tone: 'road', label: 'Đường giao thông quy hoạch', note: 'QH phân khu: phần đất giữa các ô đất (chỉ giới đường đỏ)' },
  { kind: 'fill', tone: 'road-gen', label: 'Đường chính theo QH chung', note: 'Nơi chưa có đồ án phân khu' },
  { kind: 'text', label: 'Lộ giới ≈ 21,5 m', note: 'Khoảng cách giữa ranh hai ô đất đối diện (QH phân khu, chi tiết) đo vuông góc với đường hiện trạng, từ mức phóng 16 · sai số 1–2 m' },
  { kind: 'hatch', label: 'Đất đường QH hiện chưa có đường', note: 'Đường mở mới hoặc phần mở rộng · so với OpenStreetMap, từ mức phóng 15' },
  { kind: 'fill', tone: 'bld', label: 'Công trình nằm trong đất đường QH', note: 'Có thể bị ảnh hưởng khi làm đường · Overture Maps, từ mức phóng 15' },
  { kind: 'fill', tone: 'park', label: 'Bãi đỗ xe quy hoạch', note: 'Ô đất “Đất bãi đỗ xe”' },
  { kind: 'metro', label: 'Đường sắt đô thị (metro)', note: 'Hiện trạng · OpenStreetMap' },
  { kind: 'rail', label: 'Đường sắt quốc gia', note: 'Hiện trạng · OpenStreetMap' },
  { kind: 'construction', label: 'Đường đang thi công', note: 'OpenStreetMap' },
  { kind: 'station', label: 'Ga đường sắt, metro', note: 'OpenStreetMap' },
  { kind: 'fill', tone: 'ctx', label: 'Ô đất quy hoạch (nền tham chiếu)', note: 'Màu nhạt theo chức năng' },
];

/** Project boundary overlays (toggles). */
export const BOUNDARY_LAYERS = [
  { layerNo: 659, title: 'Ranh giới đồ án phân khu', sub: 'Các đồ án quy hoạch phân khu đã số hoá', on: true },
  { layerNo: 674, title: 'Ranh giới đồ án chi tiết 1/500', sub: 'Phạm vi các đồ án chi tiết', on: true },
  { layerNo: 670, title: 'Ranh giới quy hoạch chung', sub: 'Toàn bộ địa giới hành chính Thủ đô', on: false },
];

const OFM = 'https://tiles.openfreemap.org/styles/';
export const BASEMAPS = [
  { id: 'positron', title: 'Tối giản', icon: 'map', kind: 'vector', url: OFM + 'positron' },
  { id: 'liberty', title: 'Đường phố', icon: 'route', kind: 'vector', url: OFM + 'liberty' },
  { id: 'bright', title: 'Tươi sáng', icon: 'sun', kind: 'vector', url: OFM + 'bright' },
  { id: 'dark', title: 'Nền tối', icon: 'moon', kind: 'vector', url: OFM + 'dark' },
  { id: 'imagery', title: 'Vệ tinh', icon: 'satellite', kind: 'imagery' },
  { id: 'hybrid', title: 'Vệ tinh + nhãn', icon: 'layers-2', kind: 'hybrid', labels: OFM + 'liberty' },
];
export const BASEMAP_BY_ID = Object.fromEntries(BASEMAPS.map((b) => [b.id, b]));

export const IMAGERY_TILES = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
/**
 * Same World Imagery through ArcGIS Location Platform with an API key (free tier: 2 million
 * basemap tiles / month). The key comes from /api/config (env ESRI_API_KEY on the Node server or
 * the Cloudflare Pages project); without a key the public endpoint above is used.
 */
export const IMAGERY_TILES_KEYED = 'https://ibasemaps-api.arcgis.com/arcgis/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}?token=';
export const GLYPHS = 'https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf';

export const ATTR_PLAN = '<a href="https://quyhoach.hanoi.gov.vn" target="_blank" rel="noopener">Hệ thống thông tin quy hoạch TP Hà Nội</a>';
export const ATTR_SDD = 'KHSDĐ: <a href="https://qhkhsdd.hanoi.gov.vn" target="_blank" rel="noopener">Sở Nông nghiệp và Môi trường Hà Nội</a>';
export const ATTR_OFM = '<a href="https://openfreemap.org" target="_blank" rel="noopener">OpenFreeMap</a> © <a href="https://www.openmaptiles.org/" target="_blank" rel="noopener">OpenMapTiles</a> © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>';
export const ATTR_ESRI = 'Ảnh vệ tinh © <a href="https://www.esri.com" target="_blank" rel="noopener">Esri</a>, Maxar, Earthstar Geographics';
export const ATTR_ESRI_KEYED = 'Powered by <a href="https://www.esri.com" target="_blank" rel="noopener">Esri</a> · Ảnh vệ tinh: Esri, Maxar, Earthstar Geographics, and the GIS User Community';

/** Geocoders (CORS-enabled public endpoints). */
export const ESRI_GEOCODE = 'https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer';
export const PHOTON = 'https://photon.komoot.io';

export const LEVEL_META = {
  detail: { icon: 'building', title: 'Quy hoạch chi tiết 1/500', short: 'Chi tiết 1/500' },
  zone: { icon: 'layout-grid', title: 'Quy hoạch phân khu', short: 'Phân khu' },
  general: { icon: 'map', title: 'Quy hoạch chung Thủ đô', short: 'QH chung' },
  capital: { icon: 'telescope', title: 'QH tổng thể tầm nhìn 100 năm', short: 'Tầm nhìn 100 năm' },
};

export const NEARBY_GROUPS = [
  { key: 'edu', title: 'Trường học', icon: 'school', match: /preschool|primary_school|secondary_school|high_school|school/ },
  { key: 'uni', title: 'Đại học, cao đẳng, đào tạo', icon: 'graduation-cap', match: /university|college|training_center|language_center|education/ },
  { key: 'health', title: 'Y tế', icon: 'hospital', match: /hospital|health|clinic|pharmacy|doctor/ },
  { key: 'market', title: 'Chợ, thương mại', icon: 'store', match: /market|mall|supermarket|store|shop/ },
  { key: 'gov', title: 'Cơ quan hành chính', icon: 'landmark', match: /committee|government|police|townhall|office/ },
  { key: 'other', title: 'Địa điểm khác', icon: 'map-pin', match: /./ },
  { key: 'rail', title: 'Ga đường sắt, metro', icon: 'train-front', transport: true },
  { key: 'bus', title: 'Điểm dừng xe buýt', icon: 'bus', transport: true },
  { key: 'parking', title: 'Bãi đỗ xe', icon: 'square-parking', transport: true },
  { key: 'fuel', title: 'Cây xăng', icon: 'fuel', transport: true },
];

/**
 * Land-use codes of the land-use plan maps (KHSDĐ / QHSDĐ, mã loại đất theo Luật Đất đai) with
 * the colours of the official legend (public/assets/img/khsdd-legend.webp, qhkhsdd.hanoi.gov.vn).
 */
export const SDD_LEGEND = [
  ['LUA', 'Đất trồng lúa', '#fffc82'], ['LUC', 'Đất chuyên trồng lúa nước', '#fffc8c'], ['HNK', 'Đất trồng cây hàng năm khác', '#fff0b4'],
  ['CLN', 'Đất trồng cây lâu năm', '#ffd2a0'], ['RPH', 'Đất rừng phòng hộ', '#beff1e'], ['NTS', 'Đất nuôi trồng thủy sản', '#aaffff'],
  ['NKH', 'Đất nông nghiệp khác', '#f5ffb4'], ['CQP', 'Đất quốc phòng', '#ff6450'], ['CAN', 'Đất an ninh', '#ff5046'],
  ['SKK', 'Đất khu công nghiệp', '#faaaa0'], ['SKN', 'Đất cụm công nghiệp', '#faaaa0'], ['TMD', 'Đất thương mại, dịch vụ', '#faaaa0'],
  ['SKC', 'Đất cơ sở sản xuất phi nông nghiệp', '#faaaa0'], ['DHT', 'Đất phát triển hạ tầng cấp quốc gia, cấp tỉnh, cấp huyện, cấp xã', '#ffaaa0'],
  ['DGT', 'Đất giao thông', '#ffaa32'], ['DTL', 'Đất thủy lợi', '#aaffff'], ['DVH', 'Đất xây dựng cơ sở văn hóa', '#ffaaa0'],
  ['DYT', 'Đất xây dựng cơ sở y tế', '#ffaaa0'], ['DGD', 'Đất xây dựng cơ sở giáo dục và đào tạo', '#ffaaa0'], ['DTT', 'Đất xây dựng cơ sở thể dục thể thao', '#ffaaa0'],
  ['DDT', 'Đất có di tích lịch sử – văn hóa', '#ffaaa0'], ['DDL', 'Đất danh lam thắng cảnh', '#ffaaa0'], ['DRA', 'Đất bãi thải, xử lý chất thải', '#cdaacd'],
  ['ONT', 'Đất ở tại nông thôn', '#ffd0ff'], ['ODT', 'Đất ở tại đô thị', '#ffa0ff'], ['TSC', 'Đất xây dựng trụ sở cơ quan', '#ffaaa0'],
  ['DTS', 'Đất xây dựng trụ sở của tổ chức sự nghiệp', '#faaaa0'], ['DNG', 'Đất xây dựng cơ sở ngoại giao', '#ffaaa0'], ['TON', 'Đất cơ sở tôn giáo', '#ffaaa0'],
  ['NTD', 'Đất nghĩa trang, nghĩa địa, nhà tang lễ, nhà hỏa táng', '#d2d2d2'], ['SKX', 'Đất sản xuất vật liệu xây dựng, làm đồ gốm', '#cdaacd'],
  ['DSH', 'Đất sinh hoạt cộng đồng', '#ffaaa0'], ['DKV', 'Đất khu vui chơi, giải trí công cộng', '#ffaaa0'], ['TIN', 'Đất cơ sở tín ngưỡng', '#ffaaa0'],
  ['SON', 'Đất sông, ngòi, kênh, rạch, suối', '#a0ffff'], ['MNC', 'Đất có mặt nước chuyên dùng', '#b4ffff'], ['PNK', 'Đất phi nông nghiệp khác', '#ffaaa0'],
  ['CSD', 'Đất chưa sử dụng', '#ffffff'],
];
